import StencilHTMLElement from "./stencil-element";

/**
 * ProductCtl component for offering complementary products.
 * 
 * @see {@link file://./product-ctl-card.js}
 * @see {@link file://./../../../snippets/product-ctl-card.liquid}
 * @see {@link file://./../../../snippets/product-ctl.liquid}
 */
class ProductCtl extends StencilHTMLElement {
  name = "ProductCtl";
  busy = false;

  /** @type {HTMLButtonElement|null} */
  addAllButton = null;

  ctlCards;

  constructor() {
    super();
  }

  connectedCallback() {
    // console.log(this.logPrefix, "Connected");

    this.addAllButton = this.querySelector(".btn-add-all");
    this.ctlCards = this.querySelectorAll("product-ctl-card");

    if (this.addAllButton) {
      this.addAllButton.setAttribute("aria-live", "polite");
      this.addAllButton.setAttribute("aria-busy", "false");
      this.addAllButton.addEventListener("click", (e) => this.#onAddAllClick(e));
    }

    // Update count + progress whenever a card selection toggles
    document.addEventListener("ctl-card:selection-toggle", () => this.#updateUI());

    // Initial paint
    this.#updateUI();
  }

  /** Compute selected/total, update count in SVG, aria, enable state */
  #updateUI() {
    const cards = Array.from(this.ctlCards);
    const total = cards.length;
    const selected = cards.reduce((n, c) => n + (c.isSelected ? 1 : 0), 0);

    const btn = this.addAllButton;
    if (!btn) return;

    const countText = selected === total && total > 0 ? "All" : String(selected);
    btn.querySelectorAll(".count").forEach(el => (el.textContent = countText));

    btn.disabled = selected <= 1;
    btn.setAttribute("aria-label", `Add ${selected} Products To Bag`);
  }

  async #onAddAllClick(e) {
    e.preventDefault();
    if (this.busy) return;

    this.busy = true;
    this.#startLoadingAll();

    // Build payload from selected cards only
    const selectedCards = Array.from(this.ctlCards).filter(card => card.isSelected);

    const cartPayload = selectedCards.map((card) => ({
      id: card.selectedVariantId,
      quantity: 1,
      properties: {
        _ctl_addition: card.isRequired ? "parent": "child",
        _ctl_parent: card.parentProductId ?? ""
      }
    })).reverse();

    if(cartPayload.length <= 1) {
      this.#announce("More products required");
      this.busy = false;
      this.#stopLoadingAll();
      return;
    }
    
    console.log(this.logPrefix, "Add all to cart clicked");
    console.log(this.logPrefix, "Cart payload:", cartPayload);

    try {
      await window.MP.Extensions.Cart.add(cartPayload);
      this.#announce("Added all!");
      
      const offcanvasEl = document.querySelector(".product-ctl-offcanvas");
      if (offcanvasEl) {
        bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl).hide();
      }      
    } catch (err) {
      console.error(this.logPrefix, "Add all failed:", err);
      this.#announce("Couldn’t add all. Please try again.");
    } finally {
      this.busy = false;
      this.#stopLoadingAll();
      // Refresh UI in case selection states change post-add
      this.#updateUI();
    }
  }

  #startLoadingAll() {
    if (this.addAllButton) {
      this.addAllButton.querySelector(".spinner-overlay")?.classList.remove("d-none");
      this.addAllButton.querySelector("span")?.classList.add("opacity-0");
      this.addAllButton.disabled = true;
      this.addAllButton.setAttribute("aria-busy", "true");
    }

    this.ctlCards.forEach((card) => {
      card.startLoading();
    });
  }

  #stopLoadingAll() {
    if (this.addAllButton) {
      this.addAllButton.querySelector(".spinner-overlay")?.classList.add("d-none");
      this.addAllButton.querySelector("span")?.classList.remove("opacity-0");
      this.addAllButton.disabled = false;
      this.addAllButton.setAttribute("aria-busy", "false");
    }

    this.ctlCards.forEach((card) => {
      card.stopLoading();
    });
  }

  #announce(text) {
    if (!this.addAllButton) return;
    this.addAllButton.setAttribute("aria-label", text);
  }
}

export default ProductCtl;
